import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trash2, RotateCcw } from "lucide-react";
import { Txt } from "@/components/Txt";

export default function Trash({ me }) {
  const [items, setItems] = useState([]);
  const isSuper = me?.user?.is_super_admin;

  const load = useCallback(async () => {
    const r = await api.get("/trash");
    setItems(r.data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    try { await api.post(`/trash/${id}/restore`); toast.success("Restored"); load(); }
    catch (e) { toast.error("Could not restore"); }
  };
  const purge = async (id) => {
    try { await api.delete(`/trash/${id}`); toast.success("Permanently removed"); load(); }
    catch (e) { toast.error("Could not remove"); }
  };

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-ink text-cream flex items-center justify-center"><Trash2 className="w-5 h-5" /></div>
        <h1 className="font-zilla text-3xl sm:text-4xl font-semibold"><Txt id="trash.title" screen="Trash">Trash</Txt></h1>
      </div>
      <p className="text-[17px] text-ink/70 mb-5">
        <Txt id="trash.subtitle_pre" screen="Trash">Deleted records land here and stay restorable for</Txt> <strong>30 days</strong><Txt id="trash.subtitle_post" screen="Trash">, then purge automatically. As modules ship, anything you bulk-delete will appear here first.</Txt>
      </p>

      <div className="bg-cream border border-ink/15 rounded-xl overflow-hidden" data-testid="trash-list">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Trash2 className="w-10 h-10 text-ink/25 mx-auto mb-3" />
            <p className="text-ink/55 text-[16px]"><Txt id="trash.empty" screen="Trash">Trash is empty. Nothing has been deleted yet.</Txt></p>
          </div>
        ) : (
          items.map((it) => (
            <div key={it.id} className="flex items-center justify-between gap-3 px-5 py-4 border-b border-ink/8 last:border-0" data-testid={`trash-item-${it.id}`}>
              <div>
                <div className="text-[15px] font-semibold">{it.label || it.type}</div>
                <div className="text-[13px] text-ink/55">
                  {it.type} · deleted {new Date(it.deleted_at).toLocaleDateString()} · purges {new Date(it.expires_at).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => restore(it.id)} data-testid={`trash-restore-${it.id}`} className="flex items-center gap-1.5 text-[14px] font-semibold text-dark-olive hover:underline">
                  <RotateCcw className="w-4 h-4" /> <Txt id="trash.restore" screen="Trash">Restore</Txt>
                </button>
                {isSuper && (
                  <button onClick={() => purge(it.id)} data-testid={`trash-purge-${it.id}`} className="text-[14px] font-semibold text-maroon hover:underline"><Txt id="trash.delete_forever" screen="Trash">Delete forever</Txt></button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
